'use client'

import * as Dialog from '@radix-ui/react-dialog'
import {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { Button } from '@/components/ui/button'
import { FormBuilderForm } from '@/components/FormBuilderForm'
import type { Form } from '@/payload-types'
import { FORM_QUERY_PARAM } from '@/utilities/formQuery'

/**
 * Opens the Payload form builder modal (same UX as Kontakt on the map section).
 *
 * Example: `/kontakt?form=Kontakta oss` — `form` matches the Forms **title** in Payload admin.
 */
/** @deprecated use FORM_QUERY_PARAM */
export const FORM_MODAL_QUERY_PARAM = FORM_QUERY_PARAM

const formCache = new Map<string, Form>()

function FormModalActive() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const requestedTitle =
    searchParams.get(FORM_QUERY_PARAM)?.trim() ?? ''
  const isRequested = requestedTitle.length > 0

  const [loadedForm, setLoadedForm] = useState<Form | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  /** Remount FormBuilderForm on each distinct open cycle */
  const [formMountKey, setFormMountKey] = useState(0)
  const fetchSeqRef = useRef(0)

  const stripFormModalQuery = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString())
    if (!params.has(FORM_QUERY_PARAM)) return
    params.delete(FORM_QUERY_PARAM)
    const qs = params.toString()
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
  }, [pathname, router, searchParams])

  const discardAndClose = useCallback(() => {
    setIsClosing(true)
    setConfirmClose(false)
    setLoadedForm(null)
    setLoadError(null)
    stripFormModalQuery()
  }, [stripFormModalQuery])

  const requestClose = useCallback(() => {
    if (confirmClose || !loadedForm) {
      discardAndClose()
      return
    }

    setConfirmClose(true)
  }, [confirmClose, discardAndClose, loadedForm])

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        requestClose()
      }
    },
    [requestClose],
  )

  useEffect(() => {
    if (!requestedTitle) {
      setIsClosing(false)
      setConfirmClose(false)
      setLoadedForm(null)
      setLoadError(null)
      setBusy(false)
      return
    }

    const cachedForm = formCache.get(requestedTitle)
    if (cachedForm) {
      setBusy(false)
      setConfirmClose(false)
      setLoadError(null)
      setLoadedForm(cachedForm)
      return
    }

    const seq = ++fetchSeqRef.current
    setIsClosing(false)
    setBusy(true)
    setConfirmClose(false)
    setLoadError(null)
    setLoadedForm(null)

    void (async () => {
      try {
        const qs = new URLSearchParams({
          form: requestedTitle,
        })
        const res = await fetch(`/next/form?${qs.toString()}`, {
          credentials: 'same-origin',
        })
        if (!res.ok) {
          if (seq === fetchSeqRef.current) {
            if (res.status === 404) {
              setLoadError('Hittade inget formulär med den titeln.')
            } else if (res.status === 409) {
              setLoadError('Flera formulär har samma titel. Gör titlarna unika i Payload.')
            } else {
              setLoadError('Kunde inte ladda formuläret.')
            }
            setBusy(false)
          }
          return
        }

        const data = (await res.json()) as Form
        if (seq !== fetchSeqRef.current) return
        formCache.set(requestedTitle, data)
        setLoadedForm(data)
        setFormMountKey((k) => k + 1)
      } catch {
        if (seq === fetchSeqRef.current) {
          setLoadError('Kunde inte ladda formuläret.')
        }
      } finally {
        if (seq === fetchSeqRef.current) {
          setBusy(false)
        }
      }
    })()
  }, [requestedTitle])

  if (!isRequested || isClosing) return null

  const heading =
    loadedForm?.title?.trim()
    ?? (requestedTitle || (busy ? 'Laddar…' : 'Formulär'))

  return (
    <Dialog.Root open={isRequested} onOpenChange={handleDialogOpenChange} modal>
      <Dialog.Portal>
        <Dialog.Overlay className='fixed inset-0 z-[1002] bg-black/65' />
        <Dialog.Content
          className='fixed left-1/2 top-1/2 z-[1003] max-h-[min(90vh,720px)] w-[min(92vw,520px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background p-4 text-foreground shadow-lg'
          onEscapeKeyDown={(event) => {
            event.preventDefault()
            requestClose()
          }}
          onInteractOutside={(event) => {
            event.preventDefault()
            requestClose()
          }}
        >
          <Dialog.Title className='m-0 text-xl font-semibold'>
            {heading}
          </Dialog.Title>
          <Dialog.Description className='sr-only'>
            Kontakt eller annat formulär. Fyll i fälten nedan för att skicka.
          </Dialog.Description>

          {loadError ? (
            <p className='mt-3 text-sm text-muted-foreground'>{loadError}</p>
          ) : null}

          {busy && !loadedForm ? (
            <p className='mt-3 text-sm text-muted-foreground'>Laddar formulär…</p>
          ) : null}

          {loadedForm ? (
            <>
              <div className={confirmClose ? 'hidden' : undefined}>
                <FormBuilderForm
                  key={`modal-form-${formMountKey}-${loadedForm.id}`}
                  className='mt-3 flex w-full flex-col'
                  form={loadedForm}
                  idPrefix={`form-modal-${loadedForm.id}`}
                />
              </div>

              {confirmClose ? (
                <div className='mt-4 space-y-3'>
                  <p className='text-sm text-muted-foreground'>
                    Är du säker på att du vill stänga? Det du har fyllt i formuläret kommer att försvinna.
                  </p>
                  <div className='flex flex-wrap justify-end gap-2'>
                    <Button
                      colorMode='gold'
                      size='sm'
                      type='button'
                      variant='outline'
                      onClick={() => setConfirmClose(false)}
                    >
                      Fortsätt redigera
                    </Button>
                    <Button
                      colorMode='gold'
                      size='sm'
                      type='button'
                      variant='destructive'
                      onClick={discardAndClose}
                    >
                      Stäng formuläret
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}

          {!confirmClose ? (
            <div className='mt-4 flex justify-end'>
              <Button
                colorMode='gold'
                size='sm'
                type='button'
                variant='outline'
                onClick={requestClose}
              >
                Stäng
              </Button>
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export function FormModalQueryGate() {
  return (
    <Suspense fallback={null}>
      <FormModalActive />
    </Suspense>
  )
}
